import { useState, useEffect, useRef } from "react"
import maplibregl from "maplibre-gl"
import "maplibre-gl/dist/maplibre-gl.css"
import axios from "axios"
import TRACKING_SERVICE_URL from "../../../../constants/tracking"

const INITIAL_CENTER = [77.08, 28.48]
const INITIAL_ZOOM = 12

export function useRouteMap(currentShift) {
  const mapRef = useRef(null)
  const markersRef = useRef([])
  const routePolylineRef = useRef(null)
  const mapTilerKey = import.meta.env.VITE_MAPTILER_API_KEY
  const [mapReady, setMapReady] = useState(false)

  // Initialize map once on mount
  useEffect(() => {
    if (!mapTilerKey) {
      const errorDiv = document.getElementById("route-preview-map")
      if (errorDiv) {
        errorDiv.innerHTML = `
          <div style="display: flex; align-items: center; justify-content: center; height: 100%; flex-direction: column; color: #666; padding: 20px;">
            <p style="font-size: 16px; margin-bottom: 8px;">⚠️ MapTiler API key not configured</p>
            <p style="font-size: 12px; color: #999;">Please configure VITE_MAPTILER_API_KEY in environment variables</p>
          </div>
        `
      }
      return
    }

    const initMap = () => {
      const mapContainer = document.getElementById("route-preview-map")
      if (!mapContainer) {
        setTimeout(initMap, 300)
        return
      }
      if (mapRef.current) return

      try {
        mapRef.current = new maplibregl.Map({
          container: "route-preview-map",
          style: `https://api.maptiler.com/maps/streets/style.json?key=${mapTilerKey}`,
          center: INITIAL_CENTER,
          zoom: INITIAL_ZOOM,
        })

        mapRef.current.addControl(new maplibregl.NavigationControl(), "top-right")

        mapRef.current.on('load', () => setMapReady(true))

        const onResize = () => { if (mapRef.current) mapRef.current.resize() }
        window.addEventListener('resize', onResize)
        mapRef.current._resizeHandler = onResize

        mapRef.current.on('styledata', () => {
          if (mapRef.current && mapRef.current.isStyleLoaded()) setMapReady(true)
        })

        mapRef.current.on('error', e => console.error("Map error:", e))
      } catch (error) {
        console.error("Error initializing map:", error)
        const errorDiv = document.getElementById("route-preview-map")
        if (errorDiv) {
          errorDiv.innerHTML = `
            <div style="display: flex; align-items: center; justify-content: center; height: 100%; flex-direction: column; color: #666; padding: 20px;">
              <p style="font-size: 16px; margin-bottom: 8px;">⚠️ Error initializing map</p>
              <p style="font-size: 12px; color: #999;">${error.message || 'Unknown error'}</p>
            </div>
          `
        }
      }
    }

    if (document.getElementById("route-preview-map")) {
      initMap()
    } else {
      const timer = setTimeout(initMap, 200)
      return () => clearTimeout(timer)
    }

    return () => {
      if (mapRef.current) {
        if (mapRef.current._resizeHandler) {
          window.removeEventListener('resize', mapRef.current._resizeHandler)
        }
        try { mapRef.current.remove() } catch (e) { console.error("Error removing map:", e) }
        mapRef.current = null
      }
      markersRef.current.forEach(marker => { try { marker.remove() } catch (e) {} })
      markersRef.current = []
    }
  }, [mapTilerKey])

  // Update map when stops change
  useEffect(() => {
    if (!currentShift) {
      if (mapRef.current && mapReady) {
        markersRef.current.forEach(marker => { try { marker.remove() } catch (e) {} })
        markersRef.current = []

        if (mapRef.current.getSource('route-preview')) {
          try {
            if (mapRef.current.getLayer('route-preview-line')) mapRef.current.removeLayer('route-preview-line')
            mapRef.current.removeSource('route-preview')
          } catch (e) {}
        }

        mapRef.current.flyTo({ center: INITIAL_CENTER, zoom: INITIAL_ZOOM, duration: 500 })
      }
      return
    }

    if (!mapRef.current || !mapReady) return

    const drawStraightLine = (coords) => {
      if (mapRef.current.getSource('route-preview')) {
        try {
          if (mapRef.current.getLayer('route-preview-line')) mapRef.current.removeLayer('route-preview-line')
          mapRef.current.removeSource('route-preview')
        } catch (e) {}
      }

      mapRef.current.addSource('route-preview', {
        type: 'geojson',
        data: { type: 'Feature', geometry: { type: 'LineString', coordinates: coords } },
      })
      mapRef.current.addLayer({
        id: 'route-preview-line',
        type: 'line',
        source: 'route-preview',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#4629eb', 'line-width': 4, 'line-opacity': 0.7, 'line-dasharray': [2, 2] },
      })
      routePolylineRef.current = true

      const bounds = new maplibregl.LngLatBounds()
      coords.forEach(coord => bounds.extend(coord))
      setTimeout(() => {
        if (mapRef.current) mapRef.current.fitBounds(bounds, { padding: 80, maxZoom: 16, duration: 800 })
      }, 100)
    }

    const updateMapPreview = async () => {
      if (!mapRef.current || !mapRef.current.isStyleLoaded()) {
        setTimeout(updateMapPreview, 100)
        return
      }
      try { mapRef.current.resize() } catch (_) {}

      markersRef.current.forEach(marker => { try { marker.remove() } catch (e) {} })
      markersRef.current = []

      if (mapRef.current.getSource('route-preview')) {
        try {
          if (mapRef.current.getLayer('route-preview-line')) mapRef.current.removeLayer('route-preview-line')
          mapRef.current.removeSource('route-preview')
        } catch (e) { console.error("Error removing route:", e) }
        routePolylineRef.current = null
      }

      const stops = currentShift.stops || []

      if (stops.length === 0) {
        mapRef.current.flyTo({ center: INITIAL_CENTER, zoom: INITIAL_ZOOM, duration: 500 })
        return
      }

      const validStops = stops.filter(stop => {
        const lat = parseFloat(stop.latitude)
        const lng = parseFloat(stop.longitude)
        return !isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng) && (lat !== 0 || lng !== 0)
      })

      if (validStops.length === 0) {
        mapRef.current.flyTo({ center: INITIAL_CENTER, zoom: INITIAL_ZOOM, duration: 500 })
        return
      }

      console.log(`Updating map with ${validStops.length} valid stops for shift: ${currentShift.name}`)

      const coordinates = validStops.map(stop => [parseFloat(stop.longitude), parseFloat(stop.latitude)])

      validStops.forEach((stop, index) => {
        const lat = parseFloat(stop.latitude)
        const lng = parseFloat(stop.longitude)
        const isCheckpoint = stop.isCheckpoint || false
        const markerSize = isCheckpoint ? 22 : 32
        const fontSize = isCheckpoint ? 10 : 12
        const borderWidth = isCheckpoint ? 2 : 3

        let backgroundColor
        if (isCheckpoint) backgroundColor = '#ffa500'
        else if (index === 0) backgroundColor = '#00bcd4'
        else if (index === validStops.length - 1) backgroundColor = '#ff6b35'
        else backgroundColor = '#4629eb'

        const el = document.createElement('div')
        el.className = 'stop-marker'
        el.innerHTML = `
          <div style="
            width: ${markerSize}px; height: ${markerSize}px;
            background: ${backgroundColor};
            border: ${borderWidth}px solid white; border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            font-weight: bold; color: white; font-size: ${fontSize}px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          ">${index + 1}</div>
        `

        const popupContent = isCheckpoint
          ? `<strong>${stop.name}</strong><br/>Stop ${index + 1}<br/><span style="color: #ffa500; font-weight: bold;">📍 Checkpoint</span>`
          : `<strong>${stop.name}</strong><br/>Stop ${index + 1}`

        const marker = new maplibregl.Marker({ element: el })
          .setLngLat([lng, lat])
          .setPopup(new maplibregl.Popup({ offset: 25 }).setHTML(popupContent))
          .addTo(mapRef.current)

        markersRef.current.push(marker)
      })

      console.log(`Added ${markersRef.current.length} markers to map`)

      if (coordinates.length >= 2) {
        try {
          const token = localStorage.getItem("token")
          const response = await axios.post(
            `${TRACKING_SERVICE_URL}/api/vehicle-routes/ors-directions`,
            { coordinates, profile: 'driving-car' },
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
          )

          if (response.data?.features?.length > 0) {
            const lineCoords = response.data.features[0].geometry.coordinates
            console.log(`Adding polyline with ${lineCoords.length} coordinates`)

            if (mapRef.current.getSource('route-preview')) {
              try {
                if (mapRef.current.getLayer('route-preview-line')) mapRef.current.removeLayer('route-preview-line')
                mapRef.current.removeSource('route-preview')
              } catch (e) { console.error("Error removing existing route:", e) }
            }

            mapRef.current.addSource('route-preview', {
              type: 'geojson',
              data: { type: 'Feature', geometry: { type: 'LineString', coordinates: lineCoords } },
            })
            mapRef.current.addLayer({
              id: 'route-preview-line',
              type: 'line',
              source: 'route-preview',
              layout: { 'line-join': 'round', 'line-cap': 'round' },
              paint: { 'line-color': '#4629eb', 'line-width': 4, 'line-opacity': 0.7 },
            })
            routePolylineRef.current = true
            console.log("Polyline added successfully")

            const bounds = new maplibregl.LngLatBounds()
            coordinates.forEach(coord => bounds.extend(coord))
            setTimeout(() => {
              if (mapRef.current) mapRef.current.fitBounds(bounds, { padding: 80, maxZoom: 16, duration: 800 })
            }, 100)
          } else {
            drawStraightLine(coordinates)
          }
        } catch (error) {
          console.error("Error fetching route directions:", error)
          drawStraightLine(coordinates)
        }
      } else if (coordinates.length === 1) {
        mapRef.current.flyTo({ center: coordinates[0], zoom: 15, duration: 800 })
      }
    }

    const timeoutId = setTimeout(updateMapPreview, 50)
    return () => clearTimeout(timeoutId)
  }, [
    currentShift?._id,
    currentShift?.stops?.length,
    mapReady,
    currentShift?.stops?.map(s => `${s.latitude},${s.longitude},${s.isCheckpoint}`).join('|')
  ])
}
